import { Scene, BoxGeometry, MeshBasicMaterial,NormalBlending, ShaderMaterial, WebGLRenderTarget, Mesh, LinearFilter, RGBAFormat, Vector2, Color, WebGLRenderer, OrthographicCamera, PlaneGeometry, Vector3, Object3D } from "three"
import FXAAVertex from "./FXAAVertex";
import FXAAFragment from "./FXAAFragment";


export class EdgeHighlightRenderer {
    //测试用
    private test: Mesh
    //渲染场景，用于获取遮罩
    private renderScene: Scene;
    //完整场景，用于获取遮罩
    private quadScene: Scene;
    // 用于遮罩的材质
    private maskMaterial: ShaderMaterial;
    //用于进行卷积计算获取边缘的材质
    private edgeMaterial: ShaderMaterial;
    // 遮罩的渲染目标，用于储存进行遮罩处理后得到的二维图形
    private maskBuffer: WebGLRenderTarget;
    //蒙板quad一级懵逼的材质参数
    private quad: Mesh | undefined;
    private params = {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        format: RGBAFormat,
        antialias: true
    };
    // 将边框叠加到原图片上的着色器材质
    copyMaterial: ShaderMaterial;
    private quadCamera: OrthographicCamera
    // 物体边框的缓存区
    edgeBuffer: WebGLRenderTarget;
    domElement: HTMLCanvasElement

    //遮罩及卷积材质的内部参数MaskFragment、MaskVertex、EdgeVertex、EdgeFragment
    private MaskFragment = `
      void main() {
          gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0);
      }
      `
    private MaskVertex = `
    void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
    `
    private EdgeVertex = `
      varying vec2 vUv;
      void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
      `
    private EdgeFragment = `
        uniform sampler2D maskTexture;
        uniform vec2 texSize;
        uniform vec3 color;
        uniform float thickness;
        varying vec2 vUv;
        void main() {
            vec2 invSize = thickness / texSize;
            vec4 uvOffset = vec4(1.0, 0.0, 0.0, 1.0) * vec4(invSize, invSize);
            vec4 c1 = texture2D( maskTexture, vUv + uvOffset.xy);
            vec4 c2 = texture2D( maskTexture, vUv - uvOffset.xy);
            vec4 c3 = texture2D( maskTexture, vUv + uvOffset.yw);
            vec4 c4 = texture2D( maskTexture, vUv - uvOffset.yw);     
            float diff1 = (c1.r - c2.r)*0.5;
            float diff2 = (c3.r - c4.r)*0.5;
            float d = length(vec2(diff1, diff2));
            gl_FragColor = d > 0.0 ? vec4(color, 1.0) : vec4(0.0, 0.0, 0.0, 0.0);
        }
        `
    //构造函数，用于初始化渲染用到的各个变量
    constructor(domElement: HTMLCanvasElement) {
        //测试
        const boxGeometry = new BoxGeometry(10000, 10000, 10000);
        const boxMaterial = new MeshBasicMaterial({ color: 0x00ff00 }); // 绿色
        this.test = new Mesh(boxGeometry, boxMaterial);

        this.domElement = domElement


        //实例化独立渲染场景和完整渲染场景
        this.renderScene = new Scene();
        this.quadScene = new Scene();
        //创建遮罩纹理
        this.maskBuffer = new WebGLRenderTarget(this.domElement.offsetWidth * 2, this.domElement.offsetHeight * 2, this.params);
        // 初始化遮罩材质
        this.maskMaterial = new ShaderMaterial({
            vertexShader: this.MaskVertex,
            fragmentShader: this.MaskFragment,
            depthTest: false
        });
        // 初始化卷积计算材质
        this.edgeMaterial = new ShaderMaterial({
            vertexShader: this.EdgeVertex,
            fragmentShader: this.EdgeFragment,
            uniforms: {
                maskTexture: { value: this.maskBuffer.texture },
                texSize: { value: new Vector2(this.domElement.offsetWidth * 2, this.domElement.offsetHeight * 2) },
                color: { value: new Color("#ff0000") },
                thickness: { value: 4 },
            },
            transparent: true,
            depthTest: false
        });
    }

    // 执行渲染函数
    render(renderer: WebGLRenderer, camera: OrthographicCamera, scene: Scene, s: number, aspect: number, selectedObject: Object3D) {
        renderer.clear()
        this.renderScene.clear()
        this.renderScene.add(selectedObject?.clone())


        let width = this.domElement.offsetWidth * 2;
        let height = this.domElement.offsetHeight * 2;
        // 观察选中物体边框的摄像机
        if (this.quadCamera === undefined) {
            this.quadCamera = new OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 0, 1);
            this.quadCamera.position.z = 1;
            this.quadCamera.lookAt(new Vector3());
        }

        // 选中物体边框
        if (this.quad === undefined) {
            this.quad = new Mesh(new PlaneGeometry(width, height), null);
            this.quad.frustumCulled = false;
            this.quadScene.add(this.quad);
        }
        // 物体边框的缓存区
        if (this.edgeBuffer === undefined) {
            this.edgeBuffer = new WebGLRenderTarget(width, height, this.params);
            this.edgeBuffer.texture.generateMipmaps = false;
        }
        if (this.copyMaterial === undefined) {
            this.copyMaterial = new ShaderMaterial({
                vertexShader: FXAAVertex,
                fragmentShader: FXAAFragment,
                uniforms: {
                    tDiffuse: {
                        value: this.edgeBuffer.texture
                    },
                    resolution: {
                        value: new Vector2(1 / width, 1 / height)
                    }
                },
                transparent: true,
                depthTest: false,
                blending: NormalBlending
            })
        }

        // 选中物体边框
        if (this.quad === undefined) {
            this.quad = new Mesh(new PlaneGeometry(width, height), null);
            this.quad.frustumCulled = false;
            this.quadScene.add(this.quad);
        }
        // 当前视口的摄像机
        let renderCamera = camera;
        // 记录原始状态
        let oldAutoClear = renderer.autoClear;
        let oldRenderTarget = renderer.getRenderTarget();
        // 绘制蒙版，将选中物体绘制成白色
        this.renderScene.overrideMaterial = this.maskMaterial;
        this.renderScene.background = null;
        renderer.autoClear = false;
        // 缓存白色物体和灰色背景的绘图数据
        renderer.setRenderTarget(this.maskBuffer);
        renderer.clear();
        renderer.render(this.renderScene, renderCamera);

        // 绘制边框
        // 在上面白色物体和灰色背景的画布基础上，卷积计算物体边缘
        this.quad.material = this.edgeMaterial;
        this.renderScene.overrideMaterial = null;
        // 缓存物体边框的绘图数据
        renderer.setRenderTarget(this.edgeBuffer);
        renderer.clear();
        renderer.render(this.quadScene, this.quadCamera);
        // 与原场景叠加
        this.quad.material = this.copyMaterial;
        renderer.setRenderTarget(null);
        
        // 渲染到画布上
        renderer.render(this.quadScene, this.quadCamera);
        

        // 还原原始状态
        renderer.autoClear = oldAutoClear;
        renderer.setRenderTarget(oldRenderTarget);
        renderer.render(scene, camera);


    }

}